import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import DashboardLayout from "../../../components/layout/DashboardLayout";
import {
  fetchNotificationRecipients,
  createNotificationRecipient,
  createNotificationRecipientResetState,
  deleteNotificationRecipient,
  deleteNotificationRecipientResetState,
  toggleNotificationRecipient,
  updateNotificationRecipient,
  updateNotificationRecipientResetState,
  fetchNotificationSubscriptions,
  createNotificationSubscription,
  createNotificationSubscriptionResetState,
  deleteNotificationSubscription,
  deleteNotificationSubscriptionResetState,
  toggleNotificationSubscription,
  updateNotificationSubscription,
  updateNotificationSubscriptionResetState,
  fetchBusinessUnits,
} from "../../../store/actions/admin/notifications";
import { fetchAllUsers } from "../../../store/actions/admin/users";
import styles from "./NotificationsScreen.module.scss";
import classNames from "classnames";

const NOTIFICATION_TYPES = {
  COMPANY_CHANGED: "Promena firme",
};

const SUBSCRIPTION_TYPES = {
  // camelCase keys (for form submission)
  fiscalDeviceOffline: "LPFR offline",
  fiscalDeviceOnline: "LPFR online",
  vendingControllerOffline: "ESP offline",
  vendingControllerOnline: "ESP online",
  // UPPERCASE keys (stored in database)
  FISCAL_DEVICE_OFFLINE: "LPFR offline",
  FISCAL_DEVICE_ONLINE: "LPFR online",
  VENDING_CONTROLLER_OFFLINE: "ESP offline",
  VENDING_CONTROLLER_ONLINE: "ESP online",
};

const CHANNELS = {
  Email: "Email",
  Sms: "SMS",
};

const NotificationsScreen = () => {
  const dispatch = useDispatch();

  // Recipients Modal State (Company Change)
  const [showAddRecipientModal, setShowAddRecipientModal] = useState(false);
  const [newRecipient, setNewRecipient] = useState({
    notificationType: "COMPANY_CHANGED",
    channels: { Email: true, Sms: false },
    userId: "",
    customEmail: "",
    customPhone: "",
    useCustom: false,
  });

  // Subscription Modal State (Device Status)
  const [showAddSubscriptionModal, setShowAddSubscriptionModal] = useState(false);
  const [newSubscription, setNewSubscription] = useState({
    types: { fiscalDeviceOffline: true, vendingControllerOffline: false },
    includeOnline: { fiscalDevice: false, vendingController: false }, // Whether to also create ONLINE subscriptions
    channels: { Email: true, Sms: false },
    userId: "",
    selectedBusinessUnits: [], // Multi-select for business units
    allUserCompanies: true, // Default: subscribe to all user's companies
    useCustomContact: false, // Option for custom email/phone
    customEmail: "",
    customPhone: "",
    intervalMinutes: 10,
  });

  // Edit Modal States
  const [showEditSubscriptionModal, setShowEditSubscriptionModal] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [showEditRecipientModal, setShowEditRecipientModal] = useState(false);
  const [editingRecipient, setEditingRecipient] = useState(null);

  // User subscriptions detail modal
  const [showUserSubscriptionsModal, setShowUserSubscriptionsModal] = useState(false);
  const [selectedUserSubscriptions, setSelectedUserSubscriptions] = useState(null);
  const [globalInterval, setGlobalInterval] = useState(10);
  const [selectedSubIds, setSelectedSubIds] = useState(new Set());

  // Redux state
  const recipients = useSelector((state) => state.notifications.recipients);
  const createRecipientState = useSelector((state) => state.notifications.createRecipient);
  const deleteRecipientState = useSelector((state) => state.notifications.deleteRecipient);
  const subscriptions = useSelector((state) => state.notifications.subscriptions);
  const createSubscriptionState = useSelector((state) => state.notifications.createSubscription);
  const deleteSubscriptionState = useSelector((state) => state.notifications.deleteSubscription);
  const updateSubscriptionState = useSelector((state) => state.notifications.updateSubscription);
  const updateRecipientState = useSelector((state) => state.notifications.updateRecipient);
  const businessUnits = useSelector((state) => state.notifications.businessUnits);
  const users = useSelector((state) => state.users.users);

  useEffect(() => {
    dispatch(fetchNotificationRecipients());
    dispatch(fetchNotificationSubscriptions());
    dispatch(fetchBusinessUnits());
    dispatch(fetchAllUsers(1, 1000, ""));
  }, [dispatch]);

  // Handle create recipient success
  useEffect(() => {
    if (createRecipientState.success) {
      toast.success("Primalac uspešno dodat");
      dispatch(createNotificationRecipientResetState());
      dispatch(fetchNotificationRecipients());
      setShowAddRecipientModal(false);
      resetNewRecipient();
    }
    if (createRecipientState.error) {
      toast.error("Greška pri dodavanju primaoca");
      dispatch(createNotificationRecipientResetState());
    }
  }, [createRecipientState.success, createRecipientState.error, dispatch]);

  // Handle delete recipient success
  useEffect(() => {
    if (deleteRecipientState.success) {
      toast.success("Primalac uspešno obrisan");
      dispatch(deleteNotificationRecipientResetState());
    }
    if (deleteRecipientState.error) {
      toast.error("Greška pri brisanju primaoca");
      dispatch(deleteNotificationRecipientResetState());
    }
  }, [deleteRecipientState.success, deleteRecipientState.error, dispatch]);

  // Handle create subscription success
  useEffect(() => {
    if (createSubscriptionState.success) {
      toast.success("Pretplata uspešno dodata");
      dispatch(createNotificationSubscriptionResetState());
      dispatch(fetchNotificationSubscriptions());
      setShowAddSubscriptionModal(false);
      resetNewSubscription();
    }
    if (createSubscriptionState.error) {
      toast.error("Greška pri dodavanju pretplate");
      dispatch(createNotificationSubscriptionResetState());
    }
  }, [createSubscriptionState.success, createSubscriptionState.error, dispatch]);

  // Handle delete subscription success
  useEffect(() => {
    if (deleteSubscriptionState.success) {
      toast.success("Pretplata uspešno obrisana");
      dispatch(deleteNotificationSubscriptionResetState());
      dispatch(fetchNotificationSubscriptions());
    }
    if (deleteSubscriptionState.error) {
      toast.error("Greška pri brisanju pretplate");
      dispatch(deleteNotificationSubscriptionResetState());
    }
  }, [deleteSubscriptionState.success, deleteSubscriptionState.error, dispatch]);

  // Handle update subscription success
  useEffect(() => {
    if (updateSubscriptionState.success) {
      toast.success("Pretplata uspešno izmenjena");
      dispatch(updateNotificationSubscriptionResetState());
      dispatch(fetchNotificationSubscriptions());
      setShowEditSubscriptionModal(false);
      setEditingSubscription(null);
    }
    if (updateSubscriptionState.error) {
      toast.error("Greška pri izmeni pretplate");
      dispatch(updateNotificationSubscriptionResetState());
    }
  }, [updateSubscriptionState.success, updateSubscriptionState.error, dispatch]);

  // Handle update recipient success
  useEffect(() => {
    if (updateRecipientState.success) {
      toast.success("Primalac uspešno izmenjen");
      dispatch(updateNotificationRecipientResetState());
      dispatch(fetchNotificationRecipients());
      setShowEditRecipientModal(false);
      setEditingRecipient(null);
    }
    if (updateRecipientState.error) {
      toast.error("Greška pri izmeni primaoca");
      dispatch(updateNotificationRecipientResetState());
    }
  }, [updateRecipientState.success, updateRecipientState.error, dispatch]);

  // Update modal data when subscriptions change
  useEffect(() => {
    if (selectedUserSubscriptions && subscriptions.data) {
      // Find the same group in updated data
      const groupKey = selectedUserSubscriptions.key;
      const updatedGroups = getGroupedSubscriptions();
      const updatedGroup = updatedGroups.find(g => g.key === groupKey);
      if (updatedGroup) {
        setSelectedUserSubscriptions(updatedGroup);
      } else {
        // Group no longer exists (all subscriptions deleted)
        setShowUserSubscriptionsModal(false);
        setSelectedUserSubscriptions(null);
      }
    }
  }, [subscriptions.data]);

  const resetNewRecipient = () => {
    setNewRecipient({
      notificationType: "COMPANY_CHANGED",
      channels: { Email: true, Sms: false },
      userId: "",
      customEmail: "",
      customPhone: "",
      useCustom: false,
    });
  };

  const resetNewSubscription = () => {
    setNewSubscription({
      types: { fiscalDeviceOffline: true, vendingControllerOffline: false },
      includeOnline: { fiscalDevice: false, vendingController: false },
      channels: { Email: true, Sms: false },
      userId: "",
      selectedBusinessUnits: [],
      allUserCompanies: true,
      useCustomContact: false,
      customEmail: "",
      customPhone: "",
      intervalMinutes: 10,
    });
    setUserSearch("");
    setBuSearch("");
  };

  // Create one recipient per selected channel
  const handleAddRecipient = async () => {
    const selectedChannels = Object.entries(newRecipient.channels)
      .filter(([_, selected]) => selected)
      .map(([channel]) => channel);

    if (selectedChannels.length === 0) {
      toast.error("Izaberite bar jedan kanal");
      return;
    }

    // Get selected user's email/phone if not using custom contact
    let userEmail = null;
    let userPhone = null;
    if (!newRecipient.useCustom && newRecipient.userId) {
      const selectedUser = users?.data?.find(u => u.id === parseInt(newRecipient.userId));
      if (selectedUser) {
        userEmail = selectedUser.email;
        userPhone = selectedUser.phone;
      }
    }

    for (const channel of selectedChannels) {
      const data = {
        notificationType: newRecipient.notificationType,
        channel: channel.toLowerCase(),
      };

      if (newRecipient.useCustom) {
        if (channel === "Email") {
          data.customEmail = newRecipient.customEmail;
        } else {
          data.customPhone = newRecipient.customPhone;
        }
      } else {
        // Send userId and also set customEmail/customPhone from user's data
        data.userId = parseInt(newRecipient.userId);
        if (channel === "Email") {
          data.customEmail = userEmail;
        } else {
          data.customPhone = userPhone;
        }
      }

      dispatch(createNotificationRecipient(data));
    }
  };

  // Create subscriptions based on selected options
  const handleAddSubscription = async () => {
    const selectedTypes = Object.entries(newSubscription.types)
      .filter(([_, selected]) => selected)
      .map(([type]) => type);
    const selectedChannels = Object.entries(newSubscription.channels)
      .filter(([_, selected]) => selected)
      .map(([channel]) => channel);

    if (selectedTypes.length === 0) {
      toast.error("Izaberite bar jedan tip obaveštenja");
      return;
    }
    if (selectedChannels.length === 0) {
      toast.error("Izaberite bar jedan kanal");
      return;
    }

    // Determine which business units to subscribe to
    let businessUnitIds = [];

    if (newSubscription.allUserCompanies && newSubscription.userId) {
      // Get all business units for the selected user
      const selectedUser = users.data?.find(u => u.id === parseInt(newSubscription.userId));
      if (selectedUser?.companies) {
        // Get business units from user's companies
        const userCompanyIds = selectedUser.companies.map(c => c.id);
        businessUnitIds = businessUnits.data
          ?.filter(bu => userCompanyIds.includes(bu.companyId))
          .map(bu => bu.id) || [];
      }
      if (businessUnitIds.length === 0) {
        toast.error("Korisnik nema dodeljene firme");
        return;
      }
    } else if (newSubscription.selectedBusinessUnits.length > 0) {
      businessUnitIds = newSubscription.selectedBusinessUnits;
    } else {
      toast.error("Izaberite bar jedan poslovni prostor ili opciju 'Sve firme korisnika'");
      return;
    }

    // Build list of all notification types to create (including ONLINE if selected)
    const allTypesToCreate = [];
    for (const type of selectedTypes) {
      allTypesToCreate.push(type);

      // Check if we need to also create ONLINE subscription for this type
      if (type === 'fiscalDeviceOffline' && newSubscription.includeOnline.fiscalDevice) {
        allTypesToCreate.push('fiscalDeviceOnline');
      } else if (type === 'vendingControllerOffline' && newSubscription.includeOnline.vendingController) {
        allTypesToCreate.push('vendingControllerOnline');
      }
    }

    // Create subscriptions for each business unit + type + channel combination
    for (const buId of businessUnitIds) {
      for (const type of allTypesToCreate) {
        for (const channel of selectedChannels) {
          const data = {
            businessUnitId: parseInt(buId),
            notificationType: type,
            notificationChannel: channel,
            subscribe: true,
            intervalMinutes: parseInt(newSubscription.intervalMinutes) || 10,
          };

          if (newSubscription.useCustomContact) {
            // Custom contact instead of user
            if (channel === "Email" && newSubscription.customEmail) {
              data.customEmail = newSubscription.customEmail;
            } else if (channel === "Sms" && newSubscription.customPhone) {
              data.customPhone = newSubscription.customPhone;
            }
          } else {
            if (!newSubscription.userId) {
              toast.error("Izaberite korisnika");
              return;
            }
            data.userId = parseInt(newSubscription.userId);
          }

          dispatch(createNotificationSubscription(data));
        }
      }
    }
  };

  const handleDeleteRecipient = (id) => {
    if (window.confirm("Da li ste sigurni da želite da obrišete ovog primaoca?")) {
      dispatch(deleteNotificationRecipient(id));
    }
  };

  const handleToggleRecipient = (id) => {
    dispatch(toggleNotificationRecipient(id));
  };

  const handleDeleteSubscription = (id) => {
    if (window.confirm("Da li ste sigurni da želite da obrišete ovu pretplatu?")) {
      dispatch(deleteNotificationSubscription(id));
    }
  };

  const handleDeleteAllUserSubscriptions = (group) => {
    if (window.confirm(`Da li ste sigurni da želite da obrišete sve pretplate (${group.subscriptions.length}) za korisnika ${group.label}?`)) {
      group.subscriptions.forEach(sub => {
        dispatch(deleteNotificationSubscription(sub.id));
      });
    }
  };

  const handleToggleAllUserSubscriptions = (group) => {
    // If any are active, deactivate all. If all inactive, activate all.
    const hasActive = group.activeCount > 0;
    const action = hasActive ? "deaktivirati" : "aktivirati";
    if (window.confirm(`Da li ste sigurni da želite da ${action} sve pretplate (${group.subscriptions.length}) za korisnika ${group.label}?`)) {
      group.subscriptions.forEach(sub => {
        // Only toggle if needed (skip if already in desired state)
        if (hasActive && sub.subscribed) {
          dispatch(toggleNotificationSubscription(sub.id));
        } else if (!hasActive && !sub.subscribed) {
          dispatch(toggleNotificationSubscription(sub.id));
        }
      });
    }
  };

  const handleToggleSubscription = (id) => {
    dispatch(toggleNotificationSubscription(id));
  };

  const handleApplyGlobalInterval = () => {
    if (selectedUserSubscriptions && globalInterval > 0) {
      selectedUserSubscriptions.subscriptions.forEach(sub => {
        dispatch(updateNotificationSubscription(sub.id, {
          intervalMinutes: parseInt(globalInterval),
          channel: sub.channel,
        }));
      });
    }
  };

  const handleToggleSubSelection = (subId) => {
    setSelectedSubIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(subId)) {
        newSet.delete(subId);
      } else {
        newSet.add(subId);
      }
      return newSet;
    });
  };

  const handleSelectAllSubs = () => {
    if (selectedUserSubscriptions) {
      const allIds = selectedUserSubscriptions.subscriptions.map(s => s.id);
      if (selectedSubIds.size === allIds.length) {
        setSelectedSubIds(new Set());
      } else {
        setSelectedSubIds(new Set(allIds));
      }
    }
  };

  const handleDeleteSelectedSubs = () => {
    if (selectedSubIds.size > 0) {
      if (window.confirm(`Da li ste sigurni da želite da obrišete ${selectedSubIds.size} izabranih pretplata?`)) {
        selectedSubIds.forEach(id => {
          dispatch(deleteNotificationSubscription(id));
        });
        setSelectedSubIds(new Set());
      }
    }
  };

  const handleDeactivateSelectedSubs = () => {
    if (selectedSubIds.size > 0 && selectedUserSubscriptions) {
      if (window.confirm(`Da li ste sigurni da želite da deaktivirate ${selectedSubIds.size} izabranih pretplata?`)) {
        selectedUserSubscriptions.subscriptions.forEach(sub => {
          if (selectedSubIds.has(sub.id) && sub.subscribed) {
            dispatch(toggleNotificationSubscription(sub.id));
          }
        });
        setSelectedSubIds(new Set());
      }
    }
  };

  const handleActivateSelectedSubs = () => {
    if (selectedSubIds.size > 0 && selectedUserSubscriptions) {
      if (window.confirm(`Da li ste sigurni da želite da aktivirate ${selectedSubIds.size} izabranih pretplata?`)) {
        selectedUserSubscriptions.subscriptions.forEach(sub => {
          if (selectedSubIds.has(sub.id) && !sub.subscribed) {
            dispatch(toggleNotificationSubscription(sub.id));
          }
        });
        setSelectedSubIds(new Set());
      }
    }
  };

  const handleEditSubscription = (subscription) => {
    // Capitalize channel to match dropdown options (Email, Sms)
    const channelCapitalized = subscription.channel
      ? subscription.channel.charAt(0).toUpperCase() + subscription.channel.slice(1)
      : 'Email';

    // Determine the corresponding ONLINE type for this subscription
    let onlineType = null;
    if (subscription.type === 'FISCAL_DEVICE_OFFLINE' || subscription.type === 'fiscalDeviceOffline') {
      onlineType = 'FISCAL_DEVICE_ONLINE';
    } else if (subscription.type === 'VENDING_CONTROLLER_OFFLINE' || subscription.type === 'vendingControllerOffline') {
      onlineType = 'VENDING_CONTROLLER_ONLINE';
    }

    // Check if user has a corresponding ONLINE subscription for same businessUnit and channel
    let hasOnlineSubscription = false;
    if (onlineType && subscriptions.data) {
      hasOnlineSubscription = subscriptions.data.some(sub =>
        sub.type === onlineType &&
        sub.businessUnitId === subscription.businessUnitId &&
        sub.channel === subscription.channel &&
        ((sub.userId && subscription.userId && sub.userId === subscription.userId) ||
         (sub.customEmail && subscription.customEmail && sub.customEmail === subscription.customEmail) ||
         (sub.customPhone && subscription.customPhone && sub.customPhone === subscription.customPhone))
      );
    }

    setEditingSubscription({
      id: subscription.id,
      intervalMinutes: subscription.intervalMinutes ?? 10,
      channel: channelCapitalized,
      notificationMode: hasOnlineSubscription ? 'offlineAndOnline' : 'offline',
      // Store original subscription data for creating/deleting ONLINE subscription
      originalSubscription: subscription,
      onlineType: onlineType,
      hasOnlineSubscription: hasOnlineSubscription,
    });
    setShowEditSubscriptionModal(true);
  };

  const handleSaveSubscriptionEdit = () => {
    if (editingSubscription) {
      // Update the main subscription
      dispatch(updateNotificationSubscription(editingSubscription.id, {
        intervalMinutes: parseInt(editingSubscription.intervalMinutes),
        channel: editingSubscription.channel,
      }));

      const { originalSubscription, onlineType, hasOnlineSubscription, notificationMode } = editingSubscription;

      // Handle ONLINE subscription creation/deletion based on mode change
      if (onlineType && originalSubscription) {
        const wantsOnline = notificationMode === 'offlineAndOnline';

        if (wantsOnline && !hasOnlineSubscription) {
          // Create ONLINE subscription
          const onlineData = {
            businessUnitId: originalSubscription.businessUnitId,
            notificationType: onlineType === 'FISCAL_DEVICE_ONLINE' ? 'fiscalDeviceOnline' : 'vendingControllerOnline',
            notificationChannel: editingSubscription.channel,
            subscribe: true,
            intervalMinutes: parseInt(editingSubscription.intervalMinutes),
          };
          if (originalSubscription.userId) {
            onlineData.userId = originalSubscription.userId;
          } else if (originalSubscription.customEmail) {
            onlineData.customEmail = originalSubscription.customEmail;
          } else if (originalSubscription.customPhone) {
            onlineData.customPhone = originalSubscription.customPhone;
          }
          dispatch(createNotificationSubscription(onlineData));
        } else if (!wantsOnline && hasOnlineSubscription) {
          // Delete ONLINE subscription
          const onlineSub = subscriptions.data?.find(sub =>
            sub.type === onlineType &&
            sub.businessUnitId === originalSubscription.businessUnitId &&
            sub.channel === originalSubscription.channel &&
            ((sub.userId && originalSubscription.userId && sub.userId === originalSubscription.userId) ||
             (sub.customEmail && originalSubscription.customEmail && sub.customEmail === originalSubscription.customEmail) ||
             (sub.customPhone && originalSubscription.customPhone && sub.customPhone === originalSubscription.customPhone))
          );
          if (onlineSub) {
            dispatch(deleteNotificationSubscription(onlineSub.id));
          }
        }
      }
    }
  };

  const handleEditRecipient = (recipient) => {
    // Capitalize channel to match dropdown options (Email, Sms)
    const channelCapitalized = recipient.channel
      ? recipient.channel.charAt(0).toUpperCase() + recipient.channel.slice(1)
      : 'Email';
    setEditingRecipient({
      id: recipient.id,
      channel: channelCapitalized,
      customEmail: recipient.customEmail || '',
      customPhone: recipient.customPhone || '',
    });
    setShowEditRecipientModal(true);
  };

  const handleSaveRecipientEdit = () => {
    if (editingRecipient) {
      dispatch(updateNotificationRecipient(editingRecipient.id, {
        channel: editingRecipient.channel,
        customEmail: editingRecipient.customEmail || null,
        customPhone: editingRecipient.customPhone || null,
      }));
    }
  };

  const getRecipientContact = (recipient) => {
    return recipient.customEmail || recipient.customPhone || "-";
  };

  const getRecipientUserName = (recipient) => {
    if (recipient.user) {
      return `${recipient.user.firstName} ${recipient.user.lastName}`;
    }
    return "Prilagođeni kontakt";
  };

  const getSubscriptionUserName = (sub) => {
    if (sub.user) {
      return `${sub.user.firstName} ${sub.user.lastName}`;
    }
    if (sub.customEmail) {
      return sub.customEmail;
    }
    if (sub.customPhone) {
      return sub.customPhone;
    }
    return "-";
  };

  const getSubscriptionBusinessUnit = (sub) => {
    if (sub.businessUnit) {
      const companyName = sub.businessUnit.company?.name || "";
      return `${sub.businessUnit.name}${companyName ? ` (${companyName})` : ""}`;
    }
    return "-";
  };

  // Group subscriptions by user (userId or custom contact identifier)
  const getGroupedSubscriptions = () => {
    const groups = {};

    subscriptions.data?.forEach(sub => {
      // Create a unique key for each user/contact
      let groupKey;
      let groupLabel;
      let groupEmail;
      let groupPhone;

      if (sub.user) {
        groupKey = `user_${sub.user.id}`;
        groupLabel = `${sub.user.firstName} ${sub.user.lastName}`;
        groupEmail = sub.user.email;
        groupPhone = sub.user.phone;
      } else if (sub.customEmail) {
        groupKey = `email_${sub.customEmail}`;
        groupLabel = sub.customEmail;
        groupEmail = sub.customEmail;
        groupPhone = null;
      } else if (sub.customPhone) {
        groupKey = `phone_${sub.customPhone}`;
        groupLabel = sub.customPhone;
        groupEmail = null;
        groupPhone = sub.customPhone;
      } else {
        groupKey = `unknown_${sub.id}`;
        groupLabel = "-";
        groupEmail = null;
        groupPhone = null;
      }

      if (!groups[groupKey]) {
        groups[groupKey] = {
          key: groupKey,
          label: groupLabel,
          email: groupEmail,
          phone: groupPhone,
          user: sub.user,
          subscriptions: [],
          channels: new Set(),
          types: new Set(),
          activeCount: 0,
        };
      }

      groups[groupKey].subscriptions.push(sub);
      groups[groupKey].channels.add(sub.channel);
      groups[groupKey].types.add(sub.type);
      if (sub.subscribed) {
        groups[groupKey].activeCount++;
      }
    });

    return Object.values(groups);
  };

  const handleOpenUserSubscriptions = (group) => {
    setSelectedUserSubscriptions(group);
    setSelectedSubIds(new Set()); // Reset selection when opening modal
    setShowUserSubscriptionsModal(true);
  };

  // Filter users for searchable dropdown
  const [userSearch, setUserSearch] = useState("");
  const [buSearch, setBuSearch] = useState("");
  const filteredUsers = users.data?.filter(user =>
    `${user.firstName} ${user.lastName} ${user.email}`.toLowerCase().includes(userSearch.toLowerCase())
  ).slice(0, 20) || [];
  const filteredBusinessUnits = businessUnits.data?.filter(bu =>
    `${bu.name} ${bu.company?.name || ""}`.toLowerCase().includes(buSearch.toLowerCase())
  ).slice(0, 20) || [];

  return (
    <DashboardLayout>
      <div className={styles.content}>
        <h1 className={styles.title}>Obaveštenja</h1>

        {/* Section 1: Company Change Recipients */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2>Promena firme</h2>
            <button className={styles.addButton} onClick={() => setShowAddRecipientModal(true)}>
              + Dodaj primaoca
            </button>
          </div>
          <p className={styles.sectionDescription}>
            Primaoce za obaveštenja o promeni firme na LPFR uređajima.
          </p>

          {recipients.attempt && <p>Učitavanje...</p>}

          {!recipients.attempt && recipients.data.length === 0 && (
            <p className={styles.emptyText}>Nema primaoca obaveštenja</p>
          )}

          {!recipients.attempt && recipients.data.length > 0 && (
            <div className={styles.table}>
              <div className={styles.tableHeader}>
                <div className={styles.headerCell}>Korisnik</div>
                <div className={styles.headerCell}>Kontakt</div>
                <div className={styles.headerCell}>Kanal</div>
                <div className={styles.headerCell}>Status</div>
                <div className={styles.headerCell}>Akcije</div>
              </div>
              {recipients.data.map((recipient) => (
                <div key={recipient.id} className={styles.tableRow}>
                  <div className={styles.cell}>{getRecipientUserName(recipient)}</div>
                  <div className={styles.cell}>{getRecipientContact(recipient)}</div>
                  <div className={styles.cell}>{CHANNELS[recipient.channel] || recipient.channel}</div>
                  <div className={styles.cell}>
                    <span
                      className={classNames(
                        styles.status,
                        recipient.enabled ? styles.enabled : styles.disabled
                      )}
                      onClick={() => handleToggleRecipient(recipient.id)}
                    >
                      {recipient.enabled ? "Aktivan" : "Neaktivan"}
                    </span>
                  </div>
                  <div className={styles.cell}>
                    <button
                      className={styles.editButton}
                      onClick={() => handleEditRecipient(recipient)}
                    >
                      Izmeni
                    </button>
                    <button
                      className={styles.deleteButton}
                      onClick={() => handleDeleteRecipient(recipient.id)}
                    >
                      Obriši
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Section 2: Device Status Subscriptions - Grouped by User */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2>Status uređaja</h2>
            <button className={styles.addButton} onClick={() => setShowAddSubscriptionModal(true)}>
              + Dodaj pretplatu
            </button>
          </div>
          <p className={styles.sectionDescription}>
            Pretplate korisnika na obaveštenja o statusu LPFR/ESP uređaja po poslovnim prostorima.
          </p>

          {subscriptions.attempt && <p>Učitavanje...</p>}

          {!subscriptions.attempt && subscriptions.data.length === 0 && (
            <p className={styles.emptyText}>Nema pretplata na obaveštenja</p>
          )}

          {!subscriptions.attempt && subscriptions.data.length > 0 && (
            <div className={styles.table}>
              <div className={classNames(styles.tableHeader, styles.groupedHeader)}>
                <div className={styles.headerCell}>Korisnik</div>
                <div className={styles.headerCell}>Kontakt</div>
                <div className={styles.headerCell}>Pretplate</div>
                <div className={styles.headerCell}>Kanali</div>
                <div className={styles.headerCell}>Status</div>
                <div className={styles.headerCell}>Akcije</div>
              </div>
              {getGroupedSubscriptions().map((group) => (
                <div
                  key={group.key}
                  className={classNames(styles.tableRow, styles.groupedRow, styles.clickableRow)}
                  onClick={() => handleOpenUserSubscriptions(group)}
                >
                  <div className={styles.cell}>{group.label}</div>
                  <div className={classNames(styles.cell, styles.contactCell)}>
                    {group.email && <div className={styles.contactItem}><span className={styles.contactLabel}>Email:</span> {group.email}</div>}
                    {group.phone && <div className={styles.contactItem}><span className={styles.contactLabel}>Tel:</span> {group.phone}</div>}
                  </div>
                  <div className={styles.cell}>
                    <span className={styles.badge}>{group.subscriptions.length}</span>
                  </div>
                  <div className={styles.cell}>
                    {Array.from(group.channels).map(ch => CHANNELS[ch] || ch).join(", ")}
                  </div>
                  <div className={styles.cell}>
                    <span
                      className={classNames(
                        styles.status,
                        group.activeCount > 0 ? styles.enabled : styles.disabled
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleAllUserSubscriptions(group);
                      }}
                      title={group.activeCount > 0 ? "Klikni da deaktiviraš sve" : "Klikni da aktiviraš sve"}
                    >
                      {group.activeCount}/{group.subscriptions.length} aktivno
                    </span>
                  </div>
                  <div className={styles.cell}>
                    <button
                      className={styles.editButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenUserSubscriptions(group);
                      }}
                    >
                      Izmeni
                    </button>
                    <button
                      className={styles.deleteButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteAllUserSubscriptions(group);
                      }}
                    >
                      Obriši sve
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* User Subscriptions Detail Modal */}
        {showUserSubscriptionsModal && selectedUserSubscriptions && (
          <div className={styles.modalOverlay} onClick={() => setShowUserSubscriptionsModal(false)}>
            <div className={classNames(styles.modal, styles.wideModal)} onClick={(e) => e.stopPropagation()}>
              <h2>Pretplate - {selectedUserSubscriptions.label}</h2>
              {selectedUserSubscriptions.email && (
                <p className={styles.modalSubtitle}>Email: {selectedUserSubscriptions.email}</p>
              )}
              {selectedUserSubscriptions.phone && (
                <p className={styles.modalSubtitle}>Telefon: {selectedUserSubscriptions.phone}</p>
              )}

              <div className={styles.globalIntervalRow}>
                <label>Interval za sve (minuti):</label>
                <input
                  type="number"
                  min="1"
                  value={globalInterval}
                  onChange={(e) => setGlobalInterval(e.target.value)}
                  className={styles.globalIntervalInput}
                />
                <button
                  className={styles.addButton}
                  onClick={handleApplyGlobalInterval}
                >
                  Primeni na sve
                </button>
              </div>

              <div className={styles.bulkActionsRow}>
                <label className={styles.bulkSelectLabel}>
                  <input
                    type="checkbox"
                    checked={selectedSubIds.size === selectedUserSubscriptions.subscriptions.length && selectedSubIds.size > 0}
                    onChange={handleSelectAllSubs}
                  />
                  Izaberi sve
                </label>
                {selectedSubIds.size > 0 && (
                  <>
                    <span>({selectedSubIds.size} izabrano)</span>
                    <button
                      className={styles.bulkDeleteButton}
                      onClick={handleActivateSelectedSubs}
                      style={{ backgroundColor: '#28a745', color: 'white' }}
                    >
                      Aktiviraj
                    </button>
                    <button
                      className={styles.bulkDeleteButton}
                      onClick={handleDeactivateSelectedSubs}
                      style={{ backgroundColor: '#ffc107', color: '#212529' }}
                    >
                      Deaktiviraj
                    </button>
                    <button
                      className={styles.bulkDeleteButton}
                      onClick={handleDeleteSelectedSubs}
                    >
                      Obriši
                    </button>
                  </>
                )}
              </div>

              <div className={styles.subscriptionsList}>
                {selectedUserSubscriptions.subscriptions.map((sub) => (
                  <div key={sub.id} className={styles.subscriptionItem}>
                    <input
                      type="checkbox"
                      className={styles.subscriptionCheckbox}
                      checked={selectedSubIds.has(sub.id)}
                      onChange={() => handleToggleSubSelection(sub.id)}
                    />
                    <div className={styles.subscriptionInfo}>
                      <div className={styles.subscriptionMain}>
                        <strong>{SUBSCRIPTION_TYPES[sub.type] || sub.type}</strong>
                        <span className={styles.subscriptionChannel}>
                          ({CHANNELS[sub.channel] || sub.channel})
                        </span>
                      </div>
                      <div className={styles.subscriptionDetails}>
                        <span>{getSubscriptionBusinessUnit(sub)}</span>
                        <span className={styles.separator}>•</span>
                        <span>Interval: {sub.intervalMinutes ?? 10} min</span>
                      </div>
                    </div>
                    <div className={styles.subscriptionActions}>
                      <span
                        className={classNames(
                          styles.status,
                          sub.subscribed ? styles.enabled : styles.disabled
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleSubscription(sub.id);
                        }}
                      >
                        {sub.subscribed ? "Aktivan" : "Neaktivan"}
                      </span>
                      <button
                        className={styles.editButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditSubscription(sub);
                        }}
                      >
                        Izmeni
                      </button>
                      <button
                        className={styles.deleteButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSubscription(sub.id);
                        }}
                      >
                        Obriši
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className={styles.modalActions}>
                <button
                  className={styles.cancelButton}
                  onClick={() => setShowUserSubscriptionsModal(false)}
                >
                  Zatvori
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Subscription Modal */}
        {showEditSubscriptionModal && editingSubscription && (
          <div className={styles.modalOverlay} onClick={() => setShowEditSubscriptionModal(false)}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
              <h2>Izmeni pretplatu</h2>

              {/* Notification Mode dropdown - only show for OFFLINE subscriptions */}
              {editingSubscription.onlineType && (
                <div className={styles.formGroup}>
                  <label>Režim obaveštenja</label>
                  <select
                    value={editingSubscription.notificationMode}
                    onChange={(e) =>
                      setEditingSubscription({ ...editingSubscription, notificationMode: e.target.value })
                    }
                  >
                    <option value="offline">Offline</option>
                    <option value="offlineAndOnline">Offline & Online</option>
                  </select>
                  <small className={styles.hint}>
                    {editingSubscription.notificationMode === 'offlineAndOnline'
                      ? 'Primićete obaveštenja kada uređaj ode offline i kada se vrati online'
                      : 'Primićete obaveštenja samo kada uređaj ode offline'}
                  </small>
                </div>
              )}

              <div className={styles.formGroup}>
                <label>Kanal</label>
                <select
                  value={editingSubscription.channel}
                  onChange={(e) =>
                    setEditingSubscription({ ...editingSubscription, channel: e.target.value })
                  }
                >
                  <option value="Email">Email</option>
                  <option value="Sms">SMS</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label>Interval (minuti)</label>
                <input
                  type="number"
                  min="0"
                  value={editingSubscription.intervalMinutes}
                  onChange={(e) =>
                    setEditingSubscription({ ...editingSubscription, intervalMinutes: e.target.value })
                  }
                />
                <small className={styles.hint}>Broj minuta pre slanja (0 = odmah, 30 = sačekaj 30 min, itd.)</small>
              </div>

              <div className={styles.modalActions}>
                <button
                  className={styles.cancelButton}
                  onClick={() => {
                    setShowEditSubscriptionModal(false);
                    setEditingSubscription(null);
                  }}
                >
                  Otkaži
                </button>
                <button
                  className={styles.saveButton}
                  onClick={handleSaveSubscriptionEdit}
                  disabled={updateSubscriptionState.attempt}
                >
                  {updateSubscriptionState.attempt ? "Čuvanje..." : "Sačuvaj"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Recipient Modal */}
        {showEditRecipientModal && editingRecipient && (
          <div className={styles.modalOverlay} onClick={() => setShowEditRecipientModal(false)}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
              <h2>Izmeni primaoca</h2>

              <div className={styles.formGroup}>
                <label>Kanal</label>
                <select
                  value={editingRecipient.channel}
                  onChange={(e) =>
                    setEditingRecipient({ ...editingRecipient, channel: e.target.value })
                  }
                >
                  <option value="Email">Email</option>
                  <option value="Sms">SMS</option>
                </select>
              </div>

              {editingRecipient.channel === 'Email' && (
                <div className={styles.formGroup}>
                  <label>Email adresa</label>
                  <input
                    type="email"
                    value={editingRecipient.customEmail}
                    onChange={(e) =>
                      setEditingRecipient({ ...editingRecipient, customEmail: e.target.value })
                    }
                    placeholder="primer@email.com"
                  />
                </div>
              )}

              {editingRecipient.channel === 'Sms' && (
                <div className={styles.formGroup}>
                  <label>Broj telefona</label>
                  <input
                    type="text"
                    value={editingRecipient.customPhone}
                    onChange={(e) =>
                      setEditingRecipient({ ...editingRecipient, customPhone: e.target.value })
                    }
                    placeholder="0601234567"
                  />
                </div>
              )}

              <div className={styles.modalActions}>
                <button
                  className={styles.cancelButton}
                  onClick={() => {
                    setShowEditRecipientModal(false);
                    setEditingRecipient(null);
                  }}
                >
                  Otkaži
                </button>
                <button
                  className={styles.saveButton}
                  onClick={handleSaveRecipientEdit}
                  disabled={updateRecipientState.attempt}
                >
                  {updateRecipientState.attempt ? "Čuvanje..." : "Sačuvaj"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Recipient Modal */}
        {showAddRecipientModal && (
          <div className={styles.modalOverlay} onClick={() => setShowAddRecipientModal(false)}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
              <h2>Dodaj primaoca - Promena firme</h2>

              <div className={styles.formGroup}>
                <label>Kanal (multiselect)</label>
                <div className={styles.checkboxGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={newRecipient.channels.Email}
                      onChange={(e) =>
                        setNewRecipient({
                          ...newRecipient,
                          channels: { ...newRecipient.channels, Email: e.target.checked },
                        })
                      }
                    />
                    Email
                  </label>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={newRecipient.channels.Sms}
                      onChange={(e) =>
                        setNewRecipient({
                          ...newRecipient,
                          channels: { ...newRecipient.channels, Sms: e.target.checked },
                        })
                      }
                    />
                    SMS
                  </label>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>
                  <input
                    type="checkbox"
                    checked={newRecipient.useCustom}
                    onChange={(e) =>
                      setNewRecipient({ ...newRecipient, useCustom: e.target.checked })
                    }
                  />
                  {" "}Koristi prilagođeni kontakt
                </label>
              </div>

              {!newRecipient.useCustom ? (
                <div className={styles.formGroup}>
                  <label>Izaberi korisnika</label>
                  <input
                    type="text"
                    placeholder="Pretraži korisnike..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className={styles.searchInput}
                  />
                  <div className={styles.selectableList}>
                    {filteredUsers.map((user) => (
                      <div
                        key={user.id}
                        className={classNames(
                          styles.selectableItem,
                          newRecipient.userId === String(user.id) && styles.selected
                        )}
                        onClick={() => setNewRecipient({ ...newRecipient, userId: String(user.id) })}
                      >
                        {user.firstName} {user.lastName} ({user.email})
                      </div>
                    ))}
                  </div>
                  {newRecipient.userId && (
                    <div className={styles.selectedInfo}>
                      Izabrano: {users.data?.find(u => u.id === parseInt(newRecipient.userId))?.firstName} {users.data?.find(u => u.id === parseInt(newRecipient.userId))?.lastName}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {newRecipient.channels.Email && (
                    <div className={styles.formGroup}>
                      <label>Email adresa</label>
                      <input
                        type="email"
                        value={newRecipient.customEmail}
                        onChange={(e) =>
                          setNewRecipient({ ...newRecipient, customEmail: e.target.value })
                        }
                        placeholder="primer@email.com"
                      />
                    </div>
                  )}
                  {newRecipient.channels.Sms && (
                    <div className={styles.formGroup}>
                      <label>Broj telefona</label>
                      <input
                        type="text"
                        value={newRecipient.customPhone}
                        onChange={(e) =>
                          setNewRecipient({ ...newRecipient, customPhone: e.target.value })
                        }
                        placeholder="0601234567"
                      />
                    </div>
                  )}
                </>
              )}

              <div className={styles.modalActions}>
                <button
                  className={styles.cancelButton}
                  onClick={() => {
                    setShowAddRecipientModal(false);
                    resetNewRecipient();
                    setUserSearch("");
                  }}
                >
                  Otkaži
                </button>
                <button
                  className={styles.saveButton}
                  onClick={handleAddRecipient}
                  disabled={createRecipientState.attempt}
                >
                  {createRecipientState.attempt ? "Čuvanje..." : "Sačuvaj"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Subscription Modal */}
        {showAddSubscriptionModal && (
          <div className={styles.modalOverlay} onClick={() => setShowAddSubscriptionModal(false)}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
              <h2>Dodaj pretplatu - Status uređaja</h2>

              {/* Custom contact option */}
              <div className={styles.formGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={newSubscription.useCustomContact}
                    onChange={(e) =>
                      setNewSubscription({ ...newSubscription, useCustomContact: e.target.checked, userId: "" })
                    }
                  />
                  Koristi prilagođeni kontakt (umesto korisnika)
                </label>
              </div>

              {/* User selection (if not using custom contact) */}
              {!newSubscription.useCustomContact && (
                <div className={styles.formGroup}>
                  <label>Korisnik</label>
                  <input
                    type="text"
                    placeholder="Pretraži korisnike..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className={styles.searchInput}
                  />
                  <div className={styles.selectableList}>
                    {filteredUsers.map((user) => (
                      <div
                        key={user.id}
                        className={classNames(
                          styles.selectableItem,
                          newSubscription.userId === String(user.id) && styles.selected
                        )}
                        onClick={() => setNewSubscription({ ...newSubscription, userId: String(user.id) })}
                      >
                        {user.firstName} {user.lastName} ({user.email})
                      </div>
                    ))}
                  </div>
                  {newSubscription.userId && (
                    <div className={styles.selectedInfo}>
                      Izabrano: {users.data?.find(u => u.id === parseInt(newSubscription.userId))?.firstName} {users.data?.find(u => u.id === parseInt(newSubscription.userId))?.lastName}
                    </div>
                  )}
                </div>
              )}

              {/* Custom contact fields (if using custom contact) */}
              {newSubscription.useCustomContact && (
                <>
                  <div className={styles.formGroup}>
                    <label>Email adresa</label>
                    <input
                      type="email"
                      value={newSubscription.customEmail}
                      onChange={(e) =>
                        setNewSubscription({ ...newSubscription, customEmail: e.target.value })
                      }
                      placeholder="primer@email.com"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Broj telefona</label>
                    <input
                      type="text"
                      value={newSubscription.customPhone}
                      onChange={(e) =>
                        setNewSubscription({ ...newSubscription, customPhone: e.target.value })
                      }
                      placeholder="0601234567"
                    />
                  </div>
                </>
              )}

              {/* Business unit selection */}
              <div className={styles.formGroup}>
                <label>Poslovni prostori</label>

                {/* All user companies option (only if user selected) */}
                {newSubscription.userId && !newSubscription.useCustomContact && (
                  <label className={classNames(styles.checkboxLabel, styles.highlightOption)}>
                    <input
                      type="checkbox"
                      checked={newSubscription.allUserCompanies}
                      onChange={(e) =>
                        setNewSubscription({
                          ...newSubscription,
                          allUserCompanies: e.target.checked,
                          selectedBusinessUnits: []
                        })
                      }
                    />
                    Sve firme korisnika (preporučeno)
                  </label>
                )}

                {/* Manual selection (if not using all user companies, or if using custom contact) */}
                {(!newSubscription.allUserCompanies || newSubscription.useCustomContact) && (
                  <>
                    <input
                      type="text"
                      placeholder="Pretraži poslovne prostore..."
                      value={buSearch}
                      onChange={(e) => setBuSearch(e.target.value)}
                      className={styles.searchInput}
                    />
                    {businessUnits.attempt && <p>Učitavanje...</p>}
                    {!businessUnits.attempt && (
                      <div className={styles.selectableList}>
                        {filteredBusinessUnits.length === 0 && (
                          <div className={styles.emptyMessage}>Nema rezultata</div>
                        )}
                        {filteredBusinessUnits.map((bu) => (
                          <div
                            key={bu.id}
                            className={classNames(
                              styles.selectableItem,
                              newSubscription.selectedBusinessUnits.includes(String(bu.id)) && styles.selected
                            )}
                            onClick={() => {
                              const buId = String(bu.id);
                              const isSelected = newSubscription.selectedBusinessUnits.includes(buId);
                              setNewSubscription({
                                ...newSubscription,
                                selectedBusinessUnits: isSelected
                                  ? newSubscription.selectedBusinessUnits.filter(id => id !== buId)
                                  : [...newSubscription.selectedBusinessUnits, buId]
                              });
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={newSubscription.selectedBusinessUnits.includes(String(bu.id))}
                              onChange={() => {}}
                              style={{ marginRight: '8px' }}
                            />
                            {bu.company?.name ? `${bu.company.name} - ` : ""}{bu.name}{bu.address?.name ? ` (${bu.address.name})` : ""}
                          </div>
                        ))}
                      </div>
                    )}
                    {newSubscription.selectedBusinessUnits.length > 0 && (
                      <div className={styles.selectedInfo}>
                        Izabrano: {newSubscription.selectedBusinessUnits.length} poslovnih prostora
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className={styles.formGroup}>
                <label>Tip obaveštenja</label>
                <div className={styles.checkboxGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={newSubscription.types.fiscalDeviceOffline}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setNewSubscription({
                          ...newSubscription,
                          types: { ...newSubscription.types, fiscalDeviceOffline: checked },
                          // Reset includeOnline for fiscalDevice if unchecking
                          includeOnline: checked
                            ? newSubscription.includeOnline
                            : { ...newSubscription.includeOnline, fiscalDevice: false },
                        });
                      }}
                    />
                    LPFR offline
                  </label>
                  {newSubscription.types.fiscalDeviceOffline && (
                    <label className={classNames(styles.checkboxLabel, styles.indentedOption)}>
                      <input
                        type="checkbox"
                        checked={newSubscription.includeOnline.fiscalDevice}
                        onChange={(e) =>
                          setNewSubscription({
                            ...newSubscription,
                            includeOnline: { ...newSubscription.includeOnline, fiscalDevice: e.target.checked },
                          })
                        }
                      />
                      + Online (obaveštenje kada se vrati)
                    </label>
                  )}
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={newSubscription.types.vendingControllerOffline}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setNewSubscription({
                          ...newSubscription,
                          types: { ...newSubscription.types, vendingControllerOffline: checked },
                          // Reset includeOnline for vendingController if unchecking
                          includeOnline: checked
                            ? newSubscription.includeOnline
                            : { ...newSubscription.includeOnline, vendingController: false },
                        });
                      }}
                    />
                    ESP offline
                  </label>
                  {newSubscription.types.vendingControllerOffline && (
                    <label className={classNames(styles.checkboxLabel, styles.indentedOption)}>
                      <input
                        type="checkbox"
                        checked={newSubscription.includeOnline.vendingController}
                        onChange={(e) =>
                          setNewSubscription({
                            ...newSubscription,
                            includeOnline: { ...newSubscription.includeOnline, vendingController: e.target.checked },
                          })
                        }
                      />
                      + Online (obaveštenje kada se vrati)
                    </label>
                  )}
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Kanal</label>
                <div className={styles.checkboxGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={newSubscription.channels.Email}
                      onChange={(e) =>
                        setNewSubscription({
                          ...newSubscription,
                          channels: { ...newSubscription.channels, Email: e.target.checked },
                        })
                      }
                    />
                    Email
                  </label>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={newSubscription.channels.Sms}
                      onChange={(e) =>
                        setNewSubscription({
                          ...newSubscription,
                          channels: { ...newSubscription.channels, Sms: e.target.checked },
                        })
                      }
                    />
                    SMS
                  </label>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Interval (minuti pre slanja)</label>
                <input
                  type="number"
                  min="0"
                  value={newSubscription.intervalMinutes}
                  onChange={(e) =>
                    setNewSubscription({ ...newSubscription, intervalMinutes: e.target.value })
                  }
                  placeholder="10"
                />
                <small className={styles.hint}>Broj minuta pre slanja (0 = odmah, 30 = sačekaj 30 min, itd.)</small>
              </div>

              <div className={styles.modalActions}>
                <button
                  className={styles.cancelButton}
                  onClick={() => {
                    setShowAddSubscriptionModal(false);
                    resetNewSubscription();
                  }}
                >
                  Otkaži
                </button>
                <button
                  className={styles.saveButton}
                  onClick={handleAddSubscription}
                  disabled={createSubscriptionState.attempt}
                >
                  {createSubscriptionState.attempt ? "Čuvanje..." : "Sačuvaj"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default NotificationsScreen;
